type Location = {
  city?: string
  country?: string
  name?: string
  streetLines?: {
    line1?: string
    line2?: string
    line3?: string
    line4?: string
    line5?: string
  }
  zip?: string
}

export const locationToString = (location: Location | undefined) => {
  if (location) {
    const {
      name,
      streetLines: {
        line1 = '',
        line2 = '',
        line3 = '',
        line4 = '',
        line5 = '',
      } = {},
      zip,
      city,
      country,
    } = location

    return [name, line1, line2, line3, line4, line5, zip, city, country]
      .filter(str => str && str.trim().length > 0)
      .join('\n')
  } else {
    return ''
  }
}
interface FormatDateOptions {
  withHours: boolean
}

export const getDateParts = (date: Date) => {
  const dateTimeFormat = new Intl.DateTimeFormat('en', {
    day: '2-digit',
    hour: 'numeric',
    hour12: false,
    minute: 'numeric',
    month: '2-digit',
    year: 'numeric',
  })

  const [
    { value: month },
    ,
    { value: day },
    ,
    { value: year },
    ,
    { value: hour },
    ,
    { value: minute },
  ] = dateTimeFormat.formatToParts(date)

  return {
    day,
    hour,
    minute,
    month,
    year,
  }
}

export const formatHoursAndMinutes = (date: Date) => {
  const { hour, minute } = getDateParts(date)

  return `${hour}:${minute}`
}

export const formatDate = (
  date: Date,
  separator = '-',
  options?: FormatDateOptions,
) => {
  const { day, hour, minute, month, year } = getDateParts(date)

  if (options?.withHours) {
    return `${day}${separator}${month}${separator}${year} ${hour}:${minute}`
  }

  return `${day}${separator}${month}${separator}${year}`
}

export const formatDateAsNaiveDate = (date: Date) => {
  const dateTimeFormat = new Intl.DateTimeFormat('en', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })

  const [
    { value: month },
    ,
    { value: day },
    ,
    { value: year },
  ] = dateTimeFormat.formatToParts(date)

  return `${year}-${month}-${day}`
}

export const formatDateString = (
  dateString: string | undefined,
  options?: FormatDateOptions,
) => {
  if (!dateString) return ''

  const date = new Date(dateString)

  return formatDate(date, undefined, options)
}

export const getTimeZone = () => {
  const offset = new Date().getTimezoneOffset() / -60
  return `${Intl.DateTimeFormat().resolvedOptions().timeZone} GMT ${
    offset !== 0 && offset > 0 ? `+${offset}` : offset
  }`
}

export const trimLeadingZeros = (s: string | undefined) => s?.replace(/^0+/, '')

export const trimToLengthWithEllipsis = (
  s: string | undefined,
  maxLength = 20,
) => ((s?.length || 0) < maxLength ? s : `${s!.substring(0, maxLength)}...`)

export const isStringBoolean = (s: string | undefined) =>
  s === 'true' || s === 'false'

export const isServer = () => {
  try {
    // window is not defined when this code is run server side
    // eslint-disable-next-line
    // @ts-ignore
    return !window
  } catch (_) {
    return true
  }
}

export const redirectToAccessRestricted = () => {
  // window is not defined when this code is run server side
  // eslint-disable-next-line
  // @ts-ignore
  const [, extractedLocale, app] = window.location.pathname.split('/')
  // window is not defined when this code is run server side
  // eslint-disable-next-line
  // @ts-ignore
  window.location.href = `/${extractedLocale}/${app}/access-restricted`
}

export const getUnixTimestamp = () => {
  return new Date().getTime()
}

export const refreshPage = () => {
  // window is not defined when this code is run server side
  // eslint-disable-next-line
  // @ts-ignore
  window.location.reload()
}

export const isPlural = (quantity: number) => quantity >= 0 && quantity !== 1

//if the default Accept-Language is not supported, set 'en' as fallback
export const DEFAULT_ACCEPT_LANGUAGE = 'en'
